import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faCalendarAlt, faTag, faArrowUp,
  faDatabase, faShareAlt, faClock, faLock, faUserShield,
  faEnvelope, faPhone, faMapMarkerAlt, faCookieBite, faBalanceScale
} from '@fortawesome/free-solid-svg-icons';
import '../assets/css/legal-pages.css';

const PrivacyPolicy = () => {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="legal-page">
      {/* Hero Header */}
      <div className="legal-header">
        <FontAwesomeIcon icon={faShieldAlt} className="legal-header-icon" />
        <h1>Privacy Policy</h1>
        <div className="legal-header-meta">
          <span><FontAwesomeIcon icon={faCalendarAlt} /> Effective Date: 6th March, 2026</span>
          <span><FontAwesomeIcon icon={faTag} /> Version 1.0</span>
        </div>
        <div className="legal-intro">
          This Privacy Policy explains how AutoboyExpress collects, uses, stores, and protects your personal information when you use our platform. By accessing or using AutoboyExpress, you agree to the terms of this Privacy Policy.
        </div>
      </div>

      {/* 1. Introduction */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShieldAlt} /> 1. Introduction</h2>
        <p>Welcome to AutoboyExpress. AutoboyExpress is a Nigerian gadget marketplace platform operated by Cyberninja Innovations that connects buyers and sellers for the buying, selling, and swapping of gadgets. We are committed to protecting your privacy and handling your personal data with transparency, integrity, and security.</p>
        <p>This Privacy Policy applies to all users of the AutoboyExpress website at <strong>autoboyexpress.com</strong> and our mobile application.</p>
      </div>

      {/* 2. Information We Collect */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faDatabase} /> 2. Information We Collect</h2>
        <p>We collect the following categories of personal information:</p>

        <h3>a. Identity Information</h3>
        <ul>
          <li>Full name</li>
          <li>Date of birth</li>
          <li>National Identification Number (NIN)</li>
          <li>Profile photo</li>
        </ul>

        <h3>b. Contact Information</h3>
        <ul>
          <li>Email address</li>
          <li>Phone number</li>
          <li>Delivery address</li>
        </ul>

        <h3>c. Business Information (Sellers only)</h3>
        <ul>
          <li>CAC registration number</li>
          <li>Business name</li>
          <li>Tax Identification Number (TIN)</li>
        </ul>

        <h3>d. Transaction Information</h3>
        <ul>
          <li>Purchase and swap history</li>
          <li>Payment records</li>
          <li>Escrow transaction details</li>
        </ul>

        <h3>e. Technical Information</h3>
        <ul>
          <li>IP address</li>
          <li>Device type and model</li>
          <li>Browser type and version</li>
          <li>Login timestamps and app usage data</li>
        </ul>

        <h3>f. Verification Information</h3>
        <ul>
          <li>Identity documents submitted for KYC verification</li>
          <li>BVN — collected from sellers during registration and passed directly to Monnify for bank account name verification. AutoboyExpress does not store or process BVN data directly.</li>
        </ul>
      </div>

      {/* 3. How We Collect */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faDatabase} /> 3. How We Collect Your Information</h2>
        <p>We collect your information through:</p>
        <ul>
          <li>Account registration and profile setup</li>
          <li>Product listings and transactions on the platform</li>
          <li>Identity verification process during onboarding</li>
          <li>Payment processing via Monnify, and escrow held through Pandascrow</li>
          <li>Swap offer submissions and order status interactions</li>
          <li>Automatic collection through cookies and device data</li>
        </ul>
      </div>

      {/* 4. How We Use */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faUserShield} /> 4. How We Use Your Information</h2>
        <p>We use your personal information for the following purposes:</p>
        <ul>
          <li>Verifying your identity and completing KYC requirements</li>
          <li>Processing orders, payments, and escrow transactions</li>
          <li>Coordinating logistics and delivery through FabsTech Express</li>
          <li>Sending order confirmations, delivery updates, and platform notifications via email and SMS</li>
          <li>Preventing fraud, money laundering, and suspicious activity on the platform</li>
          <li>Resolving disputes between buyers and sellers</li>
          <li>Improving our platform features and user experience</li>
          <li>Complying with legal and regulatory obligations under Nigerian law</li>
          <li>Sending platform updates and service announcements</li>
        </ul>
      </div>

      {/* 5. How We Share */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShareAlt} /> 5. How We Share Your Information</h2>
        <p>AutoboyExpress does not sell your personal data to any third party. We share your information only with the following service providers where necessary to operate the platform:</p>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr><th>Third Party</th><th>Purpose</th></tr>
            </thead>
            <tbody>
              <tr><td>Youverify</td><td>NIN and CAC identity verification</td></tr>
              <tr><td>Monnify</td><td>Payment processing, payouts, and bank account/BVN verification</td></tr>
              <tr><td>Pandascrow</td><td>Escrow — holding order funds until delivery is confirmed</td></tr>
              <tr><td>Termii</td><td>SMS and OTP delivery</td></tr>
              <tr><td>FabsTech Express</td><td>Logistics and delivery coordination</td></tr>
              <tr><td>Cloudinary</td><td>Secure image storage</td></tr>
              <tr><td>Google</td><td>Authentication via Google Sign-In</td></tr>
              <tr><td>Resend</td><td>Email notifications</td></tr>
            </tbody>
          </table>
        </div>
        <p>All third-party providers are contractually bound to protect your data and use it only for the stated purpose. We may also share your information with government and regulatory authorities where required by Nigerian law.</p>
      </div>

      {/* 6. Data Retention */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faClock} /> 6. Data Retention</h2>
        <p>We retain your personal data for the following periods:</p>
        <div className="legal-table-wrap">
          <table className="legal-table">
            <thead>
              <tr><th>Data Type</th><th>Retention Period</th></tr>
            </thead>
            <tbody>
              <tr><td>Account information</td><td>Duration of account + 5 years</td></tr>
              <tr><td>Transaction records</td><td>5 years from transaction date</td></tr>
              <tr><td>KYC documents</td><td>5 years from submission</td></tr>
              <tr><td>Verification records</td><td>5 years from verification date</td></tr>
              <tr><td>Technical/log data</td><td>12 months</td></tr>
            </tbody>
          </table>
        </div>
        <p>Data is securely deleted upon expiry of the retention period unless retention is required by applicable law.</p>
      </div>

      {/* 7. Data Security */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faLock} /> 7. Data Security</h2>
        <p>AutoboyExpress implements the following technical and organisational security measures to protect your personal data:</p>
        <ul>
          <li>SSL/TLS encryption for all data in transit between your device and our servers</li>
          <li>Encrypted storage for all sensitive data at rest</li>
          <li>Role-based access controls limiting data access to authorised personnel only</li>
          <li>Multi-factor authentication for all admin and staff accounts</li>
          <li>Regular automated backups on secure cloud infrastructure</li>
          <li>Regular security audits and vulnerability assessments</li>
        </ul>
      </div>

      {/* 8. Privacy by Design */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShieldAlt} /> 8. Privacy by Design</h2>
        <p>AutoboyExpress is built with privacy at its core. The following privacy protections are enforced on every transaction:</p>
        <ul>
          <li>Buyers never see sellers' personal addresses or contact details at any point</li>
          <li>Sellers never see buyers' personal addresses or contact details at any point</li>
          <li>All logistics coordination is handled entirely by the platform through FabsTech Express</li>
          <li>Only usernames and public profile information are visible between transacting parties</li>
          <li>All communication between buyers and sellers happens strictly within the platform — no external contact sharing is permitted</li>
        </ul>
      </div>

      {/* 9. Cookies */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faCookieBite} /> 9. Cookies</h2>
        <p>AutoboyExpress uses cookies and similar technologies to:</p>
        <ul>
          <li>Keep you logged in during your session</li>
          <li>Remember your preferences and settings</li>
          <li>Analyse platform usage and performance</li>
          <li>Improve our services and user experience</li>
        </ul>
        <p>You can control and manage cookies through your browser settings. Please note that disabling certain cookies may affect the functionality of some platform features.</p>
      </div>

      {/* 10. Your Rights */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faBalanceScale} /> 10. Your Rights</h2>
        <p>Under the Nigeria Data Protection Regulation (NDPR) 2019, you have the following rights regarding your personal data:</p>
        <ul>
          <li><strong>Right of Access</strong> — request a copy of the personal data we hold about you</li>
          <li><strong>Right to Rectification</strong> — request correction of inaccurate or incomplete data</li>
          <li><strong>Right to Erasure</strong> — request deletion of your personal data where no longer necessary</li>
          <li><strong>Right to Data Portability</strong> — receive your data in a structured, portable format</li>
          <li><strong>Right to Object</strong> — object to certain types of processing of your personal data</li>
          <li><strong>Right to Withdraw Consent</strong> — withdraw consent at any time where processing is based on consent</li>
        </ul>
        <p>To exercise any of these rights, please contact us at <a href="mailto:admin@autoboyexpress.com">admin@autoboyexpress.com</a>. We will respond to all requests within 30 days.</p>
      </div>

      {/* 11. Children's Privacy */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faUserShield} /> 11. Children's Privacy</h2>
        <p>AutoboyExpress is strictly intended for users who are <strong>18 years of age or older</strong>. We do not knowingly collect personal information from minors. If we become aware that a minor has created an account on our platform, we will immediately deactivate the account and permanently delete all associated personal data.</p>
        <p>If you believe a minor has registered on our platform, please contact us immediately at <a href="mailto:admin@autoboyexpress.com">admin@autoboyexpress.com</a>.</p>
      </div>

      {/* 12. Third Party Links */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShareAlt} /> 12. Third Party Links</h2>
        <p>Our platform may contain links to third-party websites, applications, or services. AutoboyExpress is not responsible for the privacy practices or content of these external platforms. We strongly encourage you to read the privacy policy of any third-party service before providing your personal information.</p>
      </div>

      {/* 13. Changes */}
      <div className="legal-section">
        <h2><FontAwesomeIcon icon={faShieldAlt} /> 13. Changes to This Privacy Policy</h2>
        <p>AutoboyExpress reserves the right to update or modify this Privacy Policy at any time. When changes are made, we will:</p>
        <ul>
          <li>Update the effective date at the top of this document</li>
          <li>Notify registered users via email or in-app notification</li>
          <li>Post the updated policy on our website at autoboyexpress.com/privacy-policy</li>
        </ul>
        <p>Your continued use of AutoboyExpress after any changes are posted constitutes your acceptance of the updated Privacy Policy.</p>
      </div>

      {/* Contact */}
      <div className="legal-contact">
        <h2><FontAwesomeIcon icon={faEnvelope} /> 14. Contact Us</h2>
        <div className="legal-contact-grid">
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faMapMarkerAlt} className="contact-icon" />
            <span>3/9 Olukolesho Street, Tayeolu Tower, Lagos, Nigeria</span>
          </div>
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
            <a href="mailto:support@autoboyexpress.com">support@autoboyexpress.com</a>
          </div>
          <div className="legal-contact-item">
            <FontAwesomeIcon icon={faPhone} className="contact-icon" />
            <a href="tel:09136941527">09136941527</a> / <a href="tel:08110749313">08110749313</a>
          </div>
        </div>
      </div>

      <div className="legal-back-top">
        <button onClick={scrollToTop}>
          <FontAwesomeIcon icon={faArrowUp} /> Back to Top
        </button>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
